/**
 * useCreatorAdminAnalytics - Admin-only hooks for Creator Portal
 * 
 * Provides admin capabilities:
 * - Fetch all creator accounts
 * - Fetch all training artifacts
 * - Create/update/delete creator accounts
 * - Link/unlink artifacts to creators
 * 
 * Part of Training Intelligence System (Phase 6)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { pushClientError } from '@/utils/clientErrorBuffer';

export type CreatorAccountStatus = 'invited' | 'active' | 'disabled' | null;

export interface CreatorAccount {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  provider_id: string | null;
  verified: boolean;
  is_admin: boolean;
  created_at: string;
  status: CreatorAccountStatus;
  handle: string | null;
  invite_sent_at: string | null;
  disabled_at: string | null;
  disabled_by: string | null;
  last_activity_at: string | null;
  artifact_count?: number;
  provider_name?: string | null;
}

export interface TrainingArtifact {
  id: string;
  title: string;
  provider_name: string;
  artifact_type: string;
  skill_depth: string;
  is_active: boolean;
}

export interface CreatorArtifactLink {
  id: string;
  creator_id: string;
  artifact_id: string;
  relationship: string;
}

/**
 * Check if current user is a creator admin
 */
export function useIsCreatorAdmin() {
  return useQuery({
    queryKey: ['is-creator-admin'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('training_creator_accounts')
        .select('is_admin')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return data?.is_admin ?? false;
    },
  });
}

/**
 * Fetch all creator accounts (admin only)
 */
export function useAllCreatorAccounts() {
  return useQuery({
    queryKey: ['all-creator-accounts'],
    queryFn: async () => {
      const { data: accounts, error } = await supabase
        .from('training_creator_accounts')
        .select(`
          id, user_id, name, email, provider_id, verified, is_admin, created_at, 
          status, handle, invite_sent_at, disabled_at, disabled_by, last_activity_at,
          training_providers ( id, name, display_name )
        `)
        .order('name');

      if (error) throw error;

      // Get artifact counts for each creator
      const { data: artifactLinks } = await supabase
        .from('training_creator_artifacts')
        .select('creator_id');

      const countMap = new Map<string, number>();
      (artifactLinks || []).forEach(link => {
        countMap.set(link.creator_id, (countMap.get(link.creator_id) || 0) + 1);
      });

      return (accounts || []).map(acc => ({
        ...acc,
        artifact_count: countMap.get(acc.id) || 0,
        provider_name: (acc as any).training_providers?.display_name 
          || (acc as any).training_providers?.name 
          || null,
      })) as CreatorAccount[];
    },
  });
}

/**
 * Fetch all training artifacts (for linking to creators)
 */
export function useAllTrainingArtifacts() {
  return useQuery({
    queryKey: ['all-training-artifacts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_artifacts')
        .select('id, title, provider_name, artifact_type, skill_depth, is_active')
        .order('title');

      if (error) throw error;
      return data as TrainingArtifact[];
    },
  });
}

/**
 * Fetch all active training providers (for linking to creators)
 */
export function useAllTrainingProviders() {
  return useQuery({
    queryKey: ['all-training-providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_providers')
        .select('id, name, display_name')
        .eq('is_active', true)
        .order('display_name');

      if (error) throw error;
      return data as Array<{ id: string; name: string; display_name: string }>;
    },
  });
}

/**
 * Fetch artifacts linked to a specific creator
 */
export function useCreatorArtifactLinks(creatorId: string | null) {
  return useQuery({
    queryKey: ['creator-artifact-links', creatorId],
    queryFn: async () => {
      if (!creatorId) return [];

      const { data, error } = await supabase
        .from('training_creator_artifacts')
        .select(`
          id,
          creator_id,
          artifact_id,
          relationship,
          training_artifacts (
            id,
            title,
            provider_name
          )
        `)
        .eq('creator_id', creatorId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!creatorId,
  });
}

/**
 * Create a new creator account
 *
 * FIX-R122-HIGH-1: parity with the IV-D-23 R5 audit-trail pattern used by
 * useDisableCreatorAccount + useReactivateCreatorAccount. Onboarding a new
 * creator account is an admin governance action with platform-wide impact
 * (the creator becomes attributable for artifacts; their `verified` flag
 * shapes trust signals on attributed content). Without an audit_log row,
 * there is no record of who onboarded which creator under what provider —
 * a compliance gap parallel to the disable/reactivate transitions which
 * are already audited. We write an append-only audit_log entry on success.
 */
export function useCreateCreatorAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; email: string; provider_id?: string; verified?: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: result, error } = await supabase
        .from('training_creator_accounts')
        .insert({
          name: data.name,
          email: data.email,
          provider_id: data.provider_id || null,
          verified: data.verified ?? false,
          is_admin: false,
        })
        .select()
        .single();

      if (error) throw error;

      // FIX-R122-HIGH-1: append-only audit row. We log structural facts
      // (provider attribution, verified-on-create flag) but NOT the email
      // value — email lives on the target row itself and storing it twice
      // doubles the PII surface for future purges.
      const { error: auditErr } = await supabase.from('audit_log').insert({
        actor_user_id: user?.id ?? null,
        action: 'creator_account.create',
        object_type: 'training_creator_account',
        object_id: result.id,
        new_value: {
          provider_id: data.provider_id || null,
          verified_on_create: data.verified ?? false,
          created_at: new Date().toISOString(),
        },
      });
      if (auditErr) {
        console.error('[useCreateCreatorAccount] audit_log insert failed (non-fatal):', auditErr);
      }

      // P6-A19 — canonical SCREAMING_SNAKE_CASE mirror alongside legacy.
      //
      // The legacy lowercase-dotted action `'creator_account.create'`
      // (above, FIX-R122-HIGH-1) predates the P6-A2..A18 SCREAMING_SNAKE_CASE
      // canonical-action convention. P6-A19 adds a mirror row WITHOUT
      // touching the legacy row, so any existing consumers of the
      // lowercase-dotted vocabulary keep working while compliance/triage
      // surfaces gain a row in the canonical namespace alongside
      // ACCOUNT_DEACTIVATED, SHOP_CLOSED, SHOP_AUTO_CANCELED, etc.
      //
      // ACTION VOCABULARY: 'CREATOR_ACCOUNT_CREATED' — 20th SCREAMING_SNAKE_CASE
      // entry. Single action; opens the training-platform cohort
      // (CREATOR_ACCOUNT_CREATED + CREATOR_ACCOUNT_UPDATED +
      // CREATOR_ACCOUNT_DISABLED + CREATOR_ACCOUNT_REACTIVATED — all 4
      // wired in this round across the same source file).
      //
      // Field rationale:
      //   - actor_user_id: same `user?.id ?? null` as legacy (RLS INSERT
      //     policy at migration 20260423150000 requires
      //     `actor_user_id = auth.uid()` OR `actor_user_id IS NULL`;
      //     both paths satisfy).
      //   - shop_id: null — training-platform-scoped, not shop-scoped.
      //     Same rationale as P6-A15 ACCOUNT_DEACTIVATED's account-global
      //     shop_id: null pattern; schema permits null (verified P6-A15).
      //   - object_type: 'training_creator_account' — actual schema
      //     table name (matches legacy row exactly). NOT renamed to
      //     'creator_account' — preserve consumer-table-name parity.
      //   - object_id: result.id (same as legacy row).
      //   - old_value: null — creation event; no pre-state on the
      //     training_creator_accounts row class. Same precedent
      //     P6-A12/A13/A14/A18 used. Legacy row omits old_value
      //     entirely; canonical sets explicit null for cohort
      //     consistency with P6-A workstream shape.
      //   - new_value: { provider_id, verified_on_create } — matches
      //     legacy structural fields but OMITS `created_at` (drift-
      //     avoidance precedent P6-A10/A11/A15/A16/A18 — avoid double-
      //     calling `new Date().toISOString()`; consumers join
      //     training_creator_accounts.created_at for exact timestamp).
      //   - reason: null — no admin-supplied reason text.
      //
      // INTENTIONALLY OMITTED (PII / parsimony):
      //   - email / name (preserved on the row itself; legacy omits
      //     these too).
      //   - is_admin (always `false` on create per legacy code; no
      //     value in capturing).
      //   - created_at timestamp (drift-avoidance).
      //
      // Non-blocking semantics: matches all 18 sister audit_log call
      // sites in the P6-A workstream (P6-A2..A18 use console.warn +
      // (non-fatal) marker; legacy creator-account rows above use
      // console.error — minor severity difference that's not load-
      // bearing). Legacy row IS preserved verbatim above; this canonical
      // mirror runs after it.
      const { error: canonicalAuditErr } = await supabase.from('audit_log').insert({
        actor_user_id: user?.id ?? null,
        shop_id: null,
        action: 'CREATOR_ACCOUNT_CREATED',
        object_type: 'training_creator_account',
        object_id: result.id,
        old_value: null,
        new_value: {
          provider_id: data.provider_id || null,
          verified_on_create: data.verified ?? false,
        },
        reason: null,
      });
      if (canonicalAuditErr) {
        console.warn('[useCreateCreatorAccount] canonical audit_log insert failed (non-fatal):', canonicalAuditErr);
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-creator-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['creator-artifact-links'] });
    },
  });
}

/**
 * Update a creator account
 *
 * FIX-R122-HIGH-2: parity with R122-HIGH-1 + the IV-D-23 R5 audit pattern.
 * Updates change attribution-critical fields: `name` (display string),
 * `email` (recovery + bootstrap binding), `provider_id` (which provider
 * the creator is associated with), and `verified` (trust signal on the
 * creator's attributed artifacts). Without an audit_log row there is no
 * way to investigate "who flipped this creator's verified flag" or "who
 * moved this creator to a different provider" later. We log the KEY LIST
 * of patched fields (not the values) — same contract as the
 * R52-CRIT-4 `patched_fields` summary in training-catalog-admin — so
 * sensitive fields like email are not duplicated in the audit row.
 */
export function useUpdateCreatorAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: {
      id: string;
      name?: string;
      email?: string;
      provider_id?: string | null;
      verified?: boolean
    }) => {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: result, error } = await supabase
        .from('training_creator_accounts')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // FIX-R122-HIGH-2: append-only audit row with patched-field key list.
      const patchedFields = Object.keys(data);
      const { error: auditErr } = await supabase.from('audit_log').insert({
        actor_user_id: user?.id ?? null,
        action: 'creator_account.update',
        object_type: 'training_creator_account',
        object_id: id,
        new_value: {
          patched_fields: patchedFields,
          // Capture verified flag changes explicitly since they're a trust
          // signal — but drop email/name values to avoid PII duplication.
          verified_after: data.verified,
          provider_id_after: data.provider_id,
          updated_at: new Date().toISOString(),
        },
      });
      if (auditErr) {
        console.error('[useUpdateCreatorAccount] audit_log insert failed (non-fatal):', auditErr);
      }

      // P6-A19 — canonical SCREAMING_SNAKE_CASE mirror.
      // ACTION: 'CREATOR_ACCOUNT_UPDATED' — 21st entry. See P6-A19
      // doctrinal block on useCreateCreatorAccount above for the
      // dual-write rationale, RLS / shop_id / object_type / drift-
      // avoidance discussion. patched_fields is preserved (the
      // R52-CRIT-4 contract — keys without values to avoid PII
      // duplication). updated_at OMITTED (drift-avoidance precedent).
      const { error: canonicalAuditErr } = await supabase.from('audit_log').insert({
        actor_user_id: user?.id ?? null,
        shop_id: null,
        action: 'CREATOR_ACCOUNT_UPDATED',
        object_type: 'training_creator_account',
        object_id: id,
        old_value: null,
        new_value: {
          patched_fields: patchedFields,
          verified_after: data.verified,
          provider_id_after: data.provider_id,
        },
        reason: null,
      });
      if (canonicalAuditErr) {
        console.warn('[useUpdateCreatorAccount] canonical audit_log insert failed (non-fatal):', canonicalAuditErr);
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-creator-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['creator-artifact-links'] });
    },
  });
}

/**
 * Disable a creator account (soft delete)
 */
export function useDisableCreatorAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('training_creator_accounts')
        .update({
          status: 'disabled',
          disabled_at: new Date().toISOString(),
          disabled_by: user?.id
        })
        .eq('id', creatorId);

      if (error) throw error;

      // IV-D-23 R5 FIX-1: write an audit_log row alongside the column-level
      // disabled_by attribution. Previously the only trace of who disabled
      // a creator lived in the target row itself; reactivation nulled
      // disabled_by out, erasing the history. Audit-log entries are
      // append-only and survive subsequent lifecycle transitions.
      const { error: auditErr } = await supabase.from('audit_log').insert({
        actor_user_id: user?.id ?? null,
        action: 'creator_account.disable',
        object_type: 'training_creator_account',
        object_id: creatorId,
        new_value: { status: 'disabled', disabled_at: new Date().toISOString() },
      });
      if (auditErr) {
        console.error('[useDisableCreatorAccount] audit_log insert failed (non-fatal):', auditErr);
      }

      // P6-A19 — canonical SCREAMING_SNAKE_CASE mirror.
      // ACTION: 'CREATOR_ACCOUNT_DISABLED' — 22nd entry. See P6-A19
      // doctrinal block on useCreateCreatorAccount above for the
      // dual-write rationale. status: 'disabled' literal matches the
      // training_creator_accounts.update payload. disabled_at OMITTED
      // (drift-avoidance — consumers join the row for exact timestamp).
      const { error: canonicalAuditErr } = await supabase.from('audit_log').insert({
        actor_user_id: user?.id ?? null,
        shop_id: null,
        action: 'CREATOR_ACCOUNT_DISABLED',
        object_type: 'training_creator_account',
        object_id: creatorId,
        old_value: null,
        new_value: { status: 'disabled' },
        reason: null,
      });
      if (canonicalAuditErr) {
        console.warn('[useDisableCreatorAccount] canonical audit_log insert failed (non-fatal):', canonicalAuditErr);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-creator-accounts'] });
    },
  });
}

/**
 * Reactivate a disabled creator account
 */
export function useReactivateCreatorAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorId: string) => {
      // IV-D-23 R5 FIX-1: capture actor so we can audit the reactivation.
      // Previously this mutation nulled `disabled_by` back to null and
      // wrote nothing else — a disabled-then-reactivated creator
      // appeared identical to one that had never been disabled, with
      // no trace of either lifecycle event surviving.
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('training_creator_accounts')
        .update({
          status: 'active',
          disabled_at: null,
          disabled_by: null
        })
        .eq('id', creatorId);

      if (error) throw error;

      const { error: auditErr } = await supabase.from('audit_log').insert({
        actor_user_id: user?.id ?? null,
        action: 'creator_account.reactivate',
        object_type: 'training_creator_account',
        object_id: creatorId,
        new_value: { status: 'active', reactivated_at: new Date().toISOString() },
      });
      if (auditErr) {
        console.error('[useReactivateCreatorAccount] audit_log insert failed (non-fatal):', auditErr);
      }

      // P6-A19 — canonical SCREAMING_SNAKE_CASE mirror.
      // ACTION: 'CREATOR_ACCOUNT_REACTIVATED' — 23rd entry. See P6-A19
      // doctrinal block on useCreateCreatorAccount above for the
      // dual-write rationale. status: 'active' literal matches the
      // training_creator_accounts.update payload. reactivated_at
      // OMITTED (drift-avoidance — consumers join the row's
      // disabled_at IS NULL state for the reactivation moment).
      const { error: canonicalAuditErr } = await supabase.from('audit_log').insert({
        actor_user_id: user?.id ?? null,
        shop_id: null,
        action: 'CREATOR_ACCOUNT_REACTIVATED',
        object_type: 'training_creator_account',
        object_id: creatorId,
        old_value: null,
        new_value: { status: 'active' },
        reason: null,
      });
      if (canonicalAuditErr) {
        console.warn('[useReactivateCreatorAccount] canonical audit_log insert failed (non-fatal):', canonicalAuditErr);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-creator-accounts'] });
    },
  });
}

/**
 * Send or resend invite to a creator
 */
export function useSendCreatorInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ creatorId, resend = false }: { creatorId: string; resend?: boolean }) => {
      const { data, error } = await supabase.functions.invoke('send-creator-invite', {
        body: { creatorId, resend }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-creator-accounts'] });
    },
  });
}

/**
 * Link an artifact to a creator.
 *
 * FIX-R53-CRIT-1: parity with R52-CRIT-4 audit-trail. The artifact-update /
 * archive / restore mutations route through `training-catalog-admin` edge
 * function which emits `[ADMIN-ACTION]` log lines. Linking artifact↔creator
 * is a semantically-equivalent admin action (it changes WHO is credited
 * with the artifact), but it's a direct client mutation — no edge-function
 * audit. We push to the R35 client error buffer (same pattern as the
 * View-As impersonation audit in CreatorDashboard) so the action is
 * traceable in bug reports + browser DevTools until the proper
 * admin_action_log table (R52-PROD-2) lands.
 */
export function useLinkArtifactToCreator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ creatorId, artifactId, relationship = 'creator' }: {
      creatorId: string;
      artifactId: string;
      relationship?: string;
    }) => {
      const { data, error } = await supabase
        .from('training_creator_artifacts')
        .insert({
          creator_id: creatorId,
          artifact_id: artifactId,
          relationship,
        })
        .select()
        .single();

      if (error) throw error;

      // FIX-R53-CRIT-1: audit-trail push on success.
      try {
        const { data: { user } } = await supabase.auth.getUser();
        pushClientError({
          source: 'other',
          message: `[creator-admin] link-artifact creator=${creatorId} artifact=${artifactId} rel=${relationship}`,
          userId: user?.id || null,
          route: typeof window !== 'undefined' ? window.location.pathname : undefined,
        });
      } catch {
        // never throw from audit logger
      }
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['creator-artifact-links', variables.creatorId] });
      queryClient.invalidateQueries({ queryKey: ['all-creator-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['creator-artifacts'] });
    },
  });
}

/**
 * Unlink an artifact from a creator.
 *
 * FIX-R53-CRIT-1: see useLinkArtifactToCreator above for the audit-trail
 * rationale. Unlinking is the inverse semantic of linking and shares the
 * same governance gap; same buffer push.
 */
export function useUnlinkArtifactFromCreator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ linkId, creatorId }: { linkId: string; creatorId: string }) => {
      const { error } = await supabase
        .from('training_creator_artifacts')
        .delete()
        .eq('id', linkId);

      if (error) throw error;

      // FIX-R53-CRIT-1: audit-trail push on success.
      try {
        const { data: { user } } = await supabase.auth.getUser();
        pushClientError({
          source: 'other',
          message: `[creator-admin] unlink-artifact creator=${creatorId} link=${linkId}`,
          userId: user?.id || null,
          route: typeof window !== 'undefined' ? window.location.pathname : undefined,
        });
      } catch {
        // never throw from audit logger
      }
      return { creatorId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['creator-artifact-links', result.creatorId] });
      queryClient.invalidateQueries({ queryKey: ['all-creator-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['creator-artifacts'] });
    },
  });
}

/**
 * Fetch ALL global metrics for admin network-wide summary
 */
export function useAdminNetworkSummaryStats() {
  return useQuery({
    queryKey: ['admin-network-summary-stats'],
    queryFn: async () => {
      // Get all artifacts count
      const { count: totalArtifacts } = await supabase
        .from('training_artifacts')
        .select('*', { count: 'exact', head: true });

      // Get all global metrics
      const { data: metrics, error } = await supabase
        .from('training_artifact_global_metrics')
        .select('*');

      if (error) throw error;

      // Filter to only include metrics meeting thresholds
      const metricsWithData = (metrics || []).filter(
        m => m.sample_shops >= 3 && m.sample_technicians >= 5
      );

      const totalSessions = metricsWithData.reduce((sum, m) => sum + (m.total_sessions || 0), 0);
      const totalTechnicians = metricsWithData.reduce((sum, m) => sum + (m.sample_technicians || 0), 0);
      const totalShops = metricsWithData.reduce((sum, m) => sum + (m.sample_shops || 0), 0);

      // Weighted averages
      let avgConfidenceDelta: number | null = null;
      let avgEfficiencyDelta: number | null = null;

      const confidenceDeltas = metricsWithData
        .filter(m => m.avg_confidence_delta !== null)
        .map(m => ({
          value: m.avg_confidence_delta!,
          weight: m.total_sessions || 1,
        }));

      if (confidenceDeltas.length > 0) {
        const totalWeight = confidenceDeltas.reduce((sum, d) => sum + d.weight, 0);
        avgConfidenceDelta = confidenceDeltas.reduce((sum, d) => sum + d.value * d.weight, 0) / totalWeight;
      }

      const efficiencyDeltas = metricsWithData
        .filter(m => m.avg_efficiency_delta_percent !== null)
        .map(m => ({
          value: m.avg_efficiency_delta_percent!,
          weight: m.total_sessions || 1,
        }));

      if (efficiencyDeltas.length > 0) {
        const totalWeight = efficiencyDeltas.reduce((sum, d) => sum + d.weight, 0);
        avgEfficiencyDelta = efficiencyDeltas.reduce((sum, d) => sum + d.value * d.weight, 0) / totalWeight;
      }

      const verifiedCompletions = metricsWithData.reduce((sum, m) => sum + (m.verified_completions || 0), 0);
      const likelyCompletions = metricsWithData.reduce((sum, m) => sum + (m.likely_completions || 0), 0);

      return {
        totalArtifacts: totalArtifacts || 0,
        totalSessions,
        totalTechnicians,
        totalShops,
        avgConfidenceDelta,
        avgEfficiencyDelta,
        verifiedCompletions,
        likelyCompletions,
      };
    },
  });
}
